import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  adClickFields,
  submitApplication,
  type ApplyBody,
  type ApplyRole,
} from "../lib/api";


type Props = {
  server: string;
};

const ROLE_OPTIONS: Array<{ value: ApplyRole; label: string }> = [
  { value: "pro_se", label: "Self-represented in my own case (pro se)" },
  { value: "attorney", label: "Attorney" },
  { value: "advocate", label: "Advocate (DV, court navigator, family-law)" },
  { value: "court_appointed", label: "Court-appointed (GAL, evaluator, expert)" },
  { value: "researcher", label: "Researcher or academic" },
  { value: "other", label: "Other" },
];

function readTurnstileSiteKey(): string {
  return (import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "").trim();
}
const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";
const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js";

interface TurnstileGlobal {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      callback?: (token: string) => void;
      "expired-callback"?: () => void;
      "error-callback"?: () => void;
      theme?: "auto" | "light" | "dark";
    },
  ) => string;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileGlobal;
  }
}


// Single-screen "request early access" form. Early access is concierge
// — we set up each person directly — so the form only collects what's
// needed to triage and reply: name, email, who they are, and an open
// space for their situation. Everything the old four-step wizard asked
// (case status, hearing window, message sources) now happens over email
// once we reach back out. The structured fields are still accepted by
// the API for direct callers; the form just doesn't ask for them.
export function ApplyForm({ server }: Props) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<ApplyRole | "">("");
  const [stateOrCountry, setStateOrCountry] = useState("");
  const [situation, setSituation] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  // When the user submits with a required field still empty, mark the
  // missing ones in oxblood. Clears as soon as they start typing.
  const [marksOn, setMarksOn] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  const turnstileSiteKey = readTurnstileSiteKey();
  // Resolved once on mount: an ad click carried by the URL or the
  // marketing-site cookie. Riding the body is the whole measurement
  // story (no Google JS) — see src/lib/adClick.ts. Built by the same
  // helper the mint and checkout bodies use, so the three payloads cannot
  // drift when a field is added (adding `ad_click_src` had to be done in
  // two places precisely because this used to hand-roll the object).
  const adClickBody = useMemo(() => adClickFields(), []);

  // Mount the Turnstile widget once on load — the submit button is
  // always on screen now (no wizard), so there's no later step to defer
  // to. No-op when no site key is configured (local dev).
  useEffect(() => {
    if (!turnstileSiteKey) return;
    let cancelled = false;

    function renderWidget() {
      if (cancelled) return;
      if (!turnstileContainerRef.current || !window.turnstile) return;
      setTurnstileToken(null);
      turnstileWidgetIdRef.current = window.turnstile.render(
        turnstileContainerRef.current,
        {
          sitekey: turnstileSiteKey,
          callback: (token: string) => setTurnstileToken(token),
          "expired-callback": () => setTurnstileToken(null),
          "error-callback": () => setTurnstileToken(null),
          theme: "light",
        },
      );
    }

    if (window.turnstile) {
      renderWidget();
    } else {
      let script = document.getElementById(
        TURNSTILE_SCRIPT_ID,
      ) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement("script");
        script.id = TURNSTILE_SCRIPT_ID;
        script.src = TURNSTILE_SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      const poll = window.setInterval(() => {
        if (window.turnstile) {
          window.clearInterval(poll);
          renderWidget();
        }
      }, 100);
      window.setTimeout(() => window.clearInterval(poll), 10_000);
    }

    return () => {
      cancelled = true;
      if (turnstileWidgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(turnstileWidgetIdRef.current);
        } catch {
          // already removed
        }
        turnstileWidgetIdRef.current = null;
      }
    };
  }, [turnstileSiteKey]);

  const emailValid = /^\S+@\S+\.\S+$/.test(email.trim());
  const missingName = !name.trim();
  const missingEmail = !email.trim() || !emailValid;
  const missingRole = !role;

  function markClass(missing: boolean): string {
    return marksOn && missing ? " apply-input-error" : "";
  }

  async function handleSubmit(e?: FormEvent) {
    if (e) e.preventDefault();
    if (missingName || missingEmail || missingRole) {
      setMarksOn(true);
      setError("Please add your name, a valid email, and who you are.");
      return;
    }
    if (turnstileSiteKey && !turnstileToken) {
      setError("Please complete the security check.");
      return;
    }
    if (!role) {
      // Belt-and-suspenders — missingRole above should have blocked this.
      setError("Choose the option that best describes you.");
      return;
    }

    const body: ApplyBody = {
      name: name.trim(),
      email: email.trim(),
      role,
      state_or_country: stateOrCountry.trim() || null,
      free_form_notes: situation.trim() || null,
      turnstile_token: turnstileToken,
      ...adClickBody,
    };

    setSubmitting(true);
    setError(null);
    try {
      await submitApplication(server, body);
      setSubmitted(true);
    } catch (apiErr) {
      const apiStatus = (apiErr as { status?: number })?.status;
      if (apiStatus === 429) {
        setError(
          "Too many submissions. Please wait an hour and try again.",
        );
      } else if (apiStatus === 422) {
        setError(
          "Something in the form looks off. Please double-check your email.",
        );
      } else if (apiStatus === 403) {
        setError(
          "Security check failed. Please refresh the page and try again.",
        );
      } else {
        setError("Could not submit right now. Try again in a moment.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="apply-thanks">
        <h2>Thanks.</h2>
        <p>
          We just sent a confirmation to <strong>{email}</strong> with
          what you told us — so you can sanity-check it. Reply to that
          email if anything's wrong. If it isn't in your inbox, check
          your junk or spam folder — first emails from us often
          land&nbsp;there.
        </p>
        <p>
          A real person reads every request and will email you to talk
          through what you need before anything runs — so a reply isn't
          instant; it can take a few days. When we set you up, your
          access code will come from the same&nbsp;address.
        </p>
      </div>
    );
  }

  return (
    <form className="apply-form" onSubmit={handleSubmit} noValidate>
      <label className="apply-field">
        <span className="apply-label">Your name</span>
        <input
          className={"apply-input" + markClass(missingName)}
          type="text"
          autoComplete="name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (error) setError(null);
          }}
          disabled={submitting}
          required
        />
        <span className="apply-help">
          What you'd like to be called — full name, first name, or
          nickname.
        </span>
      </label>

      <label className="apply-field">
        <span className="apply-label">Email</span>
        <input
          className={"apply-input" + markClass(missingEmail)}
          type="email"
          autoComplete="email"
          spellCheck={false}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError(null);
          }}
          disabled={submitting}
          required
        />
        <span className="apply-help">
          We'll only use this to email you about your request. We don't
          add you to any list.
        </span>
      </label>

      <label className="apply-field">
        <span className="apply-label">Which best describes you?</span>
        <select
          className={"apply-input" + markClass(missingRole)}
          value={role}
          onChange={(e) => {
            setRole(e.target.value as ApplyRole | "");
            if (error) setError(null);
          }}
          disabled={submitting}
          required
        >
          <option value="">Choose one…</option>
          {ROLE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </label>

      <label className="apply-field">
        <span className="apply-label">State or country (optional)</span>
        <input
          className="apply-input"
          type="text"
          autoComplete="off"
          value={stateOrCountry}
          onChange={(e) => setStateOrCountry(e.target.value)}
          disabled={submitting}
        />
      </label>

      <label className="apply-field">
        <span className="apply-label">
          Tell us about your situation (optional)
        </span>
        <textarea
          className="apply-input apply-textarea"
          rows={5}
          value={situation}
          onChange={(e) => setSituation(e.target.value)}
          disabled={submitting}
          placeholder="A sentence or two helps us help you — where you are in the process, what messages you have (for example an OurFamilyWizard export), and any hearing or deadline coming up."
        />
        <span className="apply-help">
          Optional, but it helps us reach back out with the right
          context. Nothing here is shared.
        </span>
      </label>

      {turnstileSiteKey ? (
        <div className="apply-turnstile" ref={turnstileContainerRef} />
      ) : null}

      {error && <div className="apply-error">{error}</div>}

      <p className="apply-reach">
        After you send this, a real person will email you to talk it
        through and get you set up — not an automated approval.
      </p>

      <button
        type="submit"
        className={submitting ? "apply-submit is-submitting" : "apply-submit"}
        disabled={submitting}
      >
        {submitting ? "Sending…" : "Request early access"}
      </button>
    </form>
  );
}
